import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { PageLoader } from '@navegasistemas/dls-navega-rjs'
import { ProtectedRoute } from '@/components/protected-route'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionRoute } from '@/components/permission-route'
import { GuestRoute } from '@/components/guest-route'
import { AppLayout } from '@/layouts/app-layout'
import { AnalyticsProvider } from '@/features/analytics/components/analytics-provider'

const LoginPage = lazy(() => import('@/pages/login-page'))
const MfaSetupPage = lazy(() => import('@/pages/mfa-setup-page'))
const MfaValidatePage = lazy(() => import('@/pages/mfa-validate-page'))
const ChangePasswordPage = lazy(() => import('@/pages/change-password-page'))
const RolesPage = lazy(() => import('@/pages/roles-page'))
const UsersPage = lazy(() => import('@/pages/users-page'))
const PreferencesPage = lazy(() => import('@/pages/preferences-page'))
const UserDetailPage = lazy(() => import('@/pages/user-detail-page'))
const UserCreatePage = lazy(() => import('@/pages/user-create-page'))
const UserImportPage = lazy(() => import('@/pages/user-import-page'))
const UsersAuditPage = lazy(() => import('@/pages/users-audit-page'))
const MyParametersPage = lazy(() => import('@/pages/my-parameters-page'))
const AccountsDashboardPage = lazy(() => import('@/pages/accounts/accounts-dashboard.page'))
const AccountsGroupsPage = lazy(() => import('@/pages/accounts/accounts-groups.page'))
const AccountsCustomAccountsPage = lazy(
  () => import('@/pages/accounts/accounts-custom-accounts.page'),
)
const AccountsCustomAccountsImportPage = lazy(
  () => import('@/pages/accounts/accounts-custom-accounts-import.page'),
)
const FileExportsPage = lazy(() => import('@/pages/file-exports-page'))
const ReportsCatalogPage = lazy(() => import('@/pages/reports/reports-catalog.page'))
const ReportDetailPage = lazy(() => import('@/pages/reports/report-detail.page'))
const GoalsPage = lazy(() => import('@/pages/goals-page'))
const GoalFormPage = lazy(() => import('@/pages/goal-form-page'))
const GoalTrackingPage = lazy(() => import('@/pages/goal-tracking-page'))
const GoalTrackingOverviewPage = lazy(() => import('@/pages/goal-tracking-overview-page'))
const GoalItemTrackingDetailPage = lazy(() => import('@/pages/goal-item-tracking-detail-page'))
const AgendasPage = lazy(() => import('@/pages/agendas-page'))
const AgendaDashboardPage = lazy(() => import('@/pages/agenda-dashboard-page'))
const AgendaDetailPage = lazy(() => import('@/pages/agenda-detail-page'))
const ImportAgendaPage = lazy(() => import('@/pages/import-agenda-page'))
const CloseAgendaPage = lazy(() => import('@/pages/close-agenda-page'))
const ManagerPanelPage = lazy(() => import('@/pages/manager-panel/manager-panel.page'))
const ManagerPanelDetailPage = lazy(() => import('@/pages/manager-panel/manager-panel-detail.page'))
const AssociateSearchPage = lazy(() => import('@/pages/associates/associate-search.page'))
const AssociatePanelPage = lazy(() => import('@/pages/associates/associate-panel.page'))
const AssociateGroupDetailPage = lazy(
  () => import('@/pages/associates/associate-group-detail.page'),
)
const AssociateIncomeHistoryPage = lazy(
  () => import('@/pages/associates/associate-income-history.page'),
)
const AssociateNotesPage = lazy(() => import('@/pages/associates/associate-notes.page'))
const AssociateCheckingAccountsListPage = lazy(
  () => import('@/pages/associates/associate-checking-accounts-list.page'),
)
const AssociateCheckingAccountsMovementsPage = lazy(
  () => import('@/pages/associates/associate-checking-accounts-movements.page'),
)
const AssociateCardsPage = lazy(() => import('@/pages/associates/associate-cards.page'))
const AssociateInsurancePage = lazy(() => import('@/pages/associates/associate-insurance.page'))
const AssociateConsortiumPage = lazy(() => import('@/pages/associates/associate-consortium.page'))
const AssociateProductHistoryPage = lazy(
  () => import('@/pages/associates/associate-product-history.page'),
)
const AssociateCreditDetailPage = lazy(
  () => import('@/pages/associates/associate-credit-detail.page'),
)

export default function App() {
  return (
    <BrowserRouter>
      <AnalyticsProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Guest-only routes — redirect to /manager-panel when already authenticated */}
            <Route element={<GuestRoute />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/mfa/setup" element={<MfaSetupPage />} />
              <Route path="/login/mfa/validate" element={<MfaValidatePage />} />
              <Route path="/login/change-password" element={<ChangePasswordPage />} />
            </Route>

            {/* Normal user routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<Navigate to="/manager-panel" replace />} />
                <Route path="/roles" element={<RolesPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/users/new" element={<UserCreatePage />} />
                <Route path="/users/import" element={<UserImportPage />} />
                <Route path="/users/audit" element={<UsersAuditPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />
                <Route path="/preferences" element={<PreferencesPage />} />
                <Route path="/parameters" element={<MyParametersPage />} />
                <Route path="/accounts" element={<AccountsDashboardPage />} />
                <Route path="/accounts/groups" element={<AccountsGroupsPage />} />
                <Route path="/accounts/custom-accounts" element={<AccountsCustomAccountsPage />} />
                <Route
                  path="/accounts/custom-accounts/import"
                  element={<AccountsCustomAccountsImportPage />}
                />
                <Route path="/exportacoes" element={<FileExportsPage />} />
                <Route path="/relatorios" element={<ReportsCatalogPage />} />
                <Route path="/relatorios/:reportType" element={<ReportDetailPage />} />
                <Route element={<PermissionRoute permission={PERMISSIONS.AGENDA_READ} />}>
                  <Route path="/agendas" element={<AgendasPage />} />
                  <Route path="/agendas/:id" element={<AgendaDetailPage />} />
                </Route>
                <Route element={<PermissionRoute permission={PERMISSIONS.AGENDA_DASHBOARD} />}>
                  <Route path="/agendas/dashboard" element={<AgendaDashboardPage />} />
                </Route>
                <Route element={<PermissionRoute permission={PERMISSIONS.AGENDA_IMPORT} />}>
                  <Route path="/agendas/import" element={<ImportAgendaPage />} />
                </Route>
                <Route element={<PermissionRoute permission={PERMISSIONS.AGENDA_CLOSE} />}>
                  <Route path="/agendas/:id/encerrar" element={<CloseAgendaPage />} />
                </Route>
                <Route path="/manager-panel" element={<ManagerPanelPage />} />
                <Route
                  path="/manager-panel/detail/:kind/:ref?"
                  element={<ManagerPanelDetailPage />}
                />
                <Route path="/associates" element={<AssociateSearchPage />} />
                <Route path="/associates/:associateId" element={<AssociatePanelPage />} />
                <Route
                  path="/associates/:associateId/registration/group"
                  element={<AssociateGroupDetailPage />}
                />
                <Route
                  path="/associates/:associateId/registration/income-history"
                  element={<AssociateIncomeHistoryPage />}
                />
                <Route
                  path="/associates/:associateId/registration/notes"
                  element={<AssociateNotesPage />}
                />
                <Route
                  path="/associates/:associateId/checking-accounts/list"
                  element={<AssociateCheckingAccountsListPage />}
                />
                <Route
                  path="/associates/:associateId/checking-accounts/movements"
                  element={<AssociateCheckingAccountsMovementsPage />}
                />
                <Route
                  path="/associates/:associateId/products/cards"
                  element={<AssociateCardsPage />}
                />
                <Route
                  path="/associates/:associateId/products/insurance"
                  element={<AssociateInsurancePage />}
                />
                <Route
                  path="/associates/:associateId/products/consortium"
                  element={<AssociateConsortiumPage />}
                />
                <Route
                  path="/associates/:associateId/products/:productType/history"
                  element={<AssociateProductHistoryPage />}
                />
                <Route
                  path="/associates/:associateId/credit/:tab"
                  element={<AssociateCreditDetailPage />}
                />
                <Route path="/goals" element={<GoalsPage />} />
                <Route path="/goals/new" element={<GoalFormPage />} />
                <Route path="/goals/:goalId/edit" element={<GoalFormPage />} />
                <Route element={<PermissionRoute permission={PERMISSIONS.GOAL_TRACK} />}>
                  <Route path="/goals/:goalId/tracking" element={<GoalTrackingPage />} />
                  <Route path="/goals/tracking" element={<GoalTrackingOverviewPage />} />
                  <Route
                    path="/goals/tracking/:goalId/:itemId"
                    element={<GoalItemTrackingDetailPage />}
                  />
                </Route>
              </Route>
            </Route>

            <Route path="/" element={<Navigate to="/manager-panel" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </AnalyticsProvider>
    </BrowserRouter>
  )
}
